import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from "dayjs/plugin/utc";
import React, { useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { Route, Routes, Navigate, NavLink, useLocation } from "react-router-dom";

import { TaskTable } from "./Containers/TaskTable";

import { listTasks } from "@/api";
import { DailyChart } from "@/components/DailyChart";
import { DailyTotal } from "@/components/DailyTotal";
import { TaskTracker } from "@/components/TaskTracker"
import { WeeklyTotal } from "@/components/WeeklyTotal";

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek)

export const TaskList: React.FC = () => {
  const location = useLocation();
  const [offsetDate, setOffsetDate] = useState<string>(dayjs().startOf("isoWeek").tz().format());
  const [limitDate, setLimitDate] = useState<string>(dayjs().endOf("day").tz().format());

  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(50);

  const { isLoading, data: taskList, refetch } = useQuery({
    queryKey: ["list-tasks", offsetDate, limitDate, offset, limit],
    queryFn: async () => await listTasks({
      offsetDate,
      limitDate,
      offset,
      limit,
    }),
  });

  return (
    <Fragment>
      <AppBar color="secondary">
        <TaskTracker refetchTableData={refetch} />
        <Divider />
        <Stack
          direction="row"
          spacing={4}
          justifyContent="space-between"
          alignItems="center"
          p={1}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateRangePicker
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{ textField: { size: "small" } }}
              disableFuture
              value={[dayjs(offsetDate), dayjs(limitDate)]}
              onChange={(value) => {
                const [offsetDateValue, limitDateValue] = value;
                if (offsetDateValue && limitDateValue) {
                  setOffsetDate(offsetDateValue?.tz().format());
                  setLimitDate(limitDateValue?.tz().format());
                }
              }}
            />
          </LocalizationProvider>
          <DailyTotal />
          <WeeklyTotal />
        </Stack>
      </AppBar>
      <Box sx={{ minHeight: "15vh" }} />
      <Stack spacing={2}>
        <Tabs
          value={location?.pathname}
          onChange={() => { }}
          indicatorColor="primary"
          textColor="inherit"
          centered
        >
          <Tab
            label="Time Sheet"
            value="/time-sheet"
            component={NavLink}
            to="/time-sheet"
          />
          <Tab
            label="Time Charts"
            value="/time-chart"
            component={NavLink}
            to="/time-chart"
          />
        </Tabs>
        <Routes>
          <Route
            path="/time-chart"
            element={(
              <DailyChart
                offsetDate={offsetDate}
                limitDate={limitDate}
              />
            )}
          />
          <Route
            path="/time-sheet"
            element={(
              <TaskTable
                refetch={refetch}
                isLoading={isLoading}
                taskList={taskList}
                offset={offset}
                limit={limit}
                setLimit={setLimit}
                setOffset={setOffset}
              />
            )}
          />
          <Route path="*" element={<Navigate to="/time-sheet" />} />
        </Routes>
      </Stack>
    </Fragment>
  )
};
